import { ReactNode, useEffect, useId, useState } from 'react'

import { CloseButton, Modal, useModalStackContext } from '@mantine/core'
import { AlertTriangle } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'

export interface DnPromptProps {
  title: string
  description?: string
  icon?: ReactNode
  onConfirm?: () => void | Promise<void>
  onCancel?: () => void
  confirmText?: string
  cancelText?: string
  variant?: 'default' | 'error' | 'warning' | 'success' | 'info'
  opened: boolean
  close: () => void
  showCloseButton?: boolean
  [key: string]: any
}

const DnPrompt = ({
  title,
  description = '',
  icon,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  opened,
  close,
  showCloseButton = true,
  ...props
}: DnPromptProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const autoStackId = useId()
  const stackId = props.stackId ?? autoStackId
  const { stackId: _stackId, ...modalProps } = props
  const stackCtx = useModalStackContext()

  // Mantine gap: Modal's stack-register useEffect has no cleanup, so
  // unmounting while opened leaks the stackId in Modal.Stack. Mirrors DeleteModal.
  useEffect(() => {
    return () => {
      if (stackCtx) {
        stackCtx.removeModal(stackId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleConfirm = async () => {
    if (onConfirm) {
      setIsLoading(true)
      try {
        await onConfirm()
      } finally {
        setIsLoading(false)
      }
    }
    close()
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
    close()
  }

  const getVariantClasses = () => {
    switch (variant) {
      case 'error':
        return {
          iconStroke: 'stroke-doubleNickel-error-600',
          buttonVariant: 'error' as const,
        }
      case 'warning':
        return {
          iconStroke: 'stroke-doubleNickel-warning-600',
          buttonVariant: 'warning' as const,
        }
      case 'success':
        return {
          iconStroke: 'stroke-doubleNickel-success-600',
          buttonVariant: 'success' as const,
        }
      case 'info':
        return {
          iconStroke: 'stroke-doubleNickel-brand-600',
          buttonVariant: 'primary' as const,
        }
      default:
        return {
          iconStroke: 'stroke-doubleNickel-gray-600',
          buttonVariant: 'primary' as const,
        }
    }
  }

  const variantClasses = getVariantClasses()

  const defaultIcon = (
    <AlertTriangle
      size={24}
      className={`rounded fill-none ${variantClasses.iconStroke}`}
    />
  )

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      size="35%"
      withCloseButton={false}
      stackId={stackId}
      {...modalProps}
    >
      <div className="flex flex-row gap-4">
        {icon || defaultIcon}
        <div className="flex flex-col gap-6">
          <div className="flex flex-row items-start justify-between">
            <div className="flex flex-row items-start gap-4">
              <div className="flex flex-col items-start gap-2">
                <div className="text-based font-semibold text-doubleNickel-gray-900">
                  {title}
                </div>
                {description && (
                  <div className="text-sm text-doubleNickel-gray-700">
                    {description}
                  </div>
                )}
              </div>
            </div>
            {showCloseButton && <CloseButton size={24} onClick={close} />}
          </div>
          <div className="flex w-full flex-row justify-start gap-3">
            {onConfirm && (
              <DNButton
                variant={variantClasses.buttonVariant}
                onClick={handleConfirm}
                loading={isLoading}
                className="min-w-20"
              >
                {confirmText}
              </DNButton>
            )}
            <DNButton variant="outline" onClick={handleCancel}>
              {cancelText}
            </DNButton>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default DnPrompt
